/**
 * 매일의 방 흐름 - 오늘의 시드 · 오늘의 카드 · 한 판의 마무리
 *
 * 근거 문서: 로드맵 확정안 4절 6번(매일의 방 · 연속 출석) · 3.1절(핀 표의 `dailyFinish`·`streakDay`) · M9 규칙 1·2·3 ·
 *           `game/daily.ts` 머리말(첫 결과만 · 출석은 끝냈을 때) · `game/roundTuning.ts` 머리말(준비물 인자가 없다)
 *
 * ## 이 파일이 있는 이유는 `main.ts`가 1200줄이기 때문이다
 *
 * 매일의 방은 화면 하나가 아니라 **작은 흐름**이다 - 오늘이 며칠인지 한 번 읽고, 그 날짜로 방을 세우고,
 * 판이 끝나면 기록·출석·핀을 한 덩어리로 확정한다. 그 넷을 엔트리포인트에 흩어 놓으면
 * "오늘 것을 두 번 세는" 종류의 버그가 배선 사이에 숨는다. `app/lobbyFlow.ts`와 같은 결정이다.
 *
 * ## 날짜는 **한 번만** 읽는다
 *
 * `todayDateKey()`는 이 모듈이 만들어질 때 한 번 불리고 그대로 굳는다(`dateKey`). 화면마다 다시 읽으면
 * 자정을 넘긴 한 판이 두 날짜에 걸치고, 그러면 "완주는 어제 방인데 출석은 오늘"이 된다
 * (`app/dateKey.ts`의 같은 경고). 굳은 값이라 자정을 넘겨 계속 켜 둔 탭은 어제의 방을 계속 준다 -
 * 그게 맞다. 판을 시작한 날의 방을 끝내는 것이지, 손에 든 방이 도중에 바뀌는 것이 아니다.
 *
 * ## 준비물이 들어올 자리가 없다
 *
 * `resolveDailyTuning`은 준비물 인자를 받지 않는다(M9 규칙 3). 그래서 이 파일에는 준비물을 넘길 수 있는
 * 코드 경로가 아예 없다 - 잊을 수 있는 게이트가 아니라 없는 인자다.
 *
 * ## 규칙은 여기 없다
 *
 * "오늘 것을 이미 했는가" · "연속이 몇 일인가" · "핀이 몇인가"는 전부 `game/daily.ts`와
 * `game/economy/pins.ts`의 순수 함수다. 이 파일은 그것들을 **정해진 차례로 부르고 한 번 저장**한다
 * (`app/accountEconomy.ts`와 같은 역할). 여기서 "어제였는가"를 다시 판단하면 규칙이 두 벌이 된다.
 */

import { DAILY_ATTEMPT, activeStreak, dailySeed, finishDaily, dailyRecordOf } from '../game/daily'
import { pinsForDaily, type PinBreakdown } from '../game/economy/pins'
import { commitDaily, type Progress } from '../game/progress'
import { resolveDailyTuning } from '../game/roundTuning'
import type { RoundOutcome } from '../game/session'
import type { Tuning } from '../game/tuning'
import type { DailyCardView } from '../ui/dailyCard'
import { todayDateKey } from './dateKey'
import { resolveDateKeyOverride } from './urlState'

export type DailyFlowDeps = Readonly<{
  /** 주소창 질의문. 튜닝 덮어쓰기(`?watch=`)와 날짜 덮어쓰기(`?date=`)를 함께 읽는다. */
  search: string
  getProgress: () => Progress
  setProgress: (next: Progress) => void
  /** 핀을 계정에 더한다(`accountEconomy.earn`). 저장은 그쪽이 한다. */
  earnPins: (amount: number) => void
}>

export type DailyFlow = Readonly<{
  /** 오늘의 날짜 키(`YYYY-MM-DD`). **부팅에 한 번 정해지고 바뀌지 않는다**(머리말). */
  dateKey: string
  /** 오늘의 방 시드. 같은 날짜 키를 받은 사람은 모두 같은 방이다(M9 규칙 1). */
  seed: string
  /** 오늘의 방 시도 번호. 고정 0이라 같은 날 모두가 같은 이상한 곳을 찾는다. */
  attempt: number
  /** 오늘의 방 튜닝. **준비물이 얹히지 않는다**(머리말). URL 덮어쓰기는 그대로 받는다. */
  tuning: () => Tuning
  /** 단계 선택 맨 위 카드가 그릴 값. 저장을 읽기만 한다. */
  cardView: () => DailyCardView
  /**
   * 한 판을 끝냈다. 기록·출석·핀이 여기서 한 번에 확정된다.
   * 그날의 첫 완주가 아니면 `null`이고 저장은 한 글자도 움직이지 않는다(연습이었다).
   */
  finish: (outcome: RoundOutcome) => PinBreakdown | null
}>

export function createDailyFlow(deps: DailyFlowDeps): DailyFlow {
  /*
   * 오늘. `?dev=1&date=`가 있으면 그쪽이 이긴다 - 연속 출석은 날이 바뀌어야 보이는 기능이라
   * 그 손잡이가 없으면 사람 손으로는 확인할 방법이 없다. 개발 도구 뒤에 있는 이유는 이 손잡이가
   * 저장을 건드리기 때문이다(`app/urlState.ts`의 `resolveDateKeyOverride` 머리말).
   */
  const dateKey = resolveDateKeyOverride(deps.search) ?? todayDateKey()

  function cardView(): DailyCardView {
    const daily = deps.getProgress().daily
    const record = dailyRecordOf(daily, dateKey)
    return {
      dateKey,
      /*
       * 저장된 `streak`이 아니라 **오늘 기준으로 살아 있는** 값을 넘긴다(`game/daily.activeStreak`).
       * 저장값은 마지막 출석일 기준이라 사흘 쉰 사람의 카드가 아직 "연속 5일"이라고 적고, 그 판을
       * 끝내는 순간 규칙이 1로 접는다 - 화면이 약속한 수와 실제로 받는 핀이 달라진다.
       * 판단은 여기서 하지 않는다. 규칙 모듈의 함수를 부를 뿐이다(머리말).
       */
      streak: activeStreak(daily, dateKey),
      record:
        record === undefined
          ? null
          : {
              score: record.score,
              foundCount: record.foundCount,
              // 모르는 분모는 **비운 채로** 넘긴다. 0으로 메우면 "0개 중 0개"라는 없던 기록이 생긴다.
              ...(record.changeCount === null ? {} : { changeCount: record.changeCount }),
            },
    }
  }

  return {
    dateKey,
    seed: dailySeed(dateKey),
    attempt: DAILY_ATTEMPT,
    tuning: () => resolveDailyTuning(null, deps.search),
    cardView,

    finish(outcome: RoundOutcome): PinBreakdown | null {
      const progress = deps.getProgress()
      const finished = finishDaily(progress.daily, dateKey, {
        score: outcome.score,
        foundCount: outcome.foundCount,
        changeCount: outcome.changeCount,
        elapsedMs: outcome.elapsedMs,
      })
      // 첫 완주가 아니면 상태가 참조 동일이다(`daily.finishDaily`). 저장도 핀도 건너뛴다 - 연습에는 보상이 없다.
      if (!finished.first) return null

      const committed = commitDaily(progress, finished.state)
      deps.setProgress(committed.progress)
      if (!committed.saved) {
        console.warn('[main] 매일의 방 기록을 저장하지 못했다. 이번 세션에서는 그대로 간다.')
      }

      /*
       * 핀은 단계 결과와 **다른 산식**이다(`pinsForDaily`) - 완주 하나와 연속 일수뿐이고 발견 수는 세지 않는다.
       * 저장이 실패해도 핀은 준다. 그쪽은 `accountEconomy`가 따로 저장하고, "기록은 못 남겼는데 핀도 없다"가
       * 한 판에서 두 가지를 잃는 쪽이라 더 나쁘다(`main.saveOutcome`의 같은 판단).
       *
       * 완주 핀에 점수 문턱을 두지 않는 것은 의도다. 매일의 방에는 단계가 없어 클리어 기준이 없고
       * (`resolveDailyTuning(null, ...)`), 하루에 한 번뿐이라 "들어가서 가만히 있기"로 벌 수 있는 것은
       * 하루 10핀이다. 문턱을 두면 그날의 유일한 판을 실수로 날린 사람에게 벌을 주게 된다.
       *
       * 연속 핀은 **출석으로 센 판에만** 붙는다(`attended`). 지나간 날의 판은 기록만 남고 출석은
       * 전진하지 않으므로 그때의 `streak`은 예전 값이고, 그것으로 핀을 주면 지난 날짜를 훑는 것만으로
       * 연속 핀이 반복된다.
       */
      const pins = pinsForDaily({ finished: true, streakDays: finished.attended ? finished.streak : 0 })
      deps.earnPins(pins.total)
      return pins
    },
  }
}
